import { AxiosError, AxiosResponse, isAxiosError } from 'axios';

import { toastService } from '@thefarmersfront/kitchen-mobile';

import {
  COMMON_CUSTOM_ERROR_CODE,
  COMMON_ERROR_MESSAGE_INTERNAL_SERVER_ERROR,
  COMMON_ERROR_MESSAGE_SYNTAX_ERROR,
  COMMON_ERROR_MESSAGE_TIMEOUT,
  COMMON_INTERNAL_SERVER_ERROR_CODE,
  COMMON_SYNTAX_ERROR_CODE,
  FORBIDDEN_ERROR_STATUS,
  INVALID_REFRESH_TOKEN_ERROR_CODE,
  TIMEOUT_ERROR_CODE,
  UNAUTHORIZED_ERROR_STATUS,
} from '~/constants';
import { useLoadingStore } from '~/store';

const setLoading = (loading: boolean) => {
  useLoadingStore.getState().actions.setLoading({ show: loading, dimmed: true, zIndex: 99999 });
};

export type TypeErrorFormat = {
  code: string;
  message: string;
  data?: unknown;
};

/**
 * 반환된 에러를 { code, message } 형식으로 변환하는 함수
 * @param error - 반환된 에러
 */
export const errorFormatter = (error: unknown): TypeErrorFormat => {
  if (isAxiosError(error)) {
    const errorResponse = error.response?.data;

    const errorStatus = String(error.response?.status);
    const errorCode = errorResponse?.code ?? COMMON_CUSTOM_ERROR_CODE;
    const errorMessage = errorResponse?.message ?? COMMON_ERROR_MESSAGE_SYNTAX_ERROR;
    const errorData = errorResponse?.data ?? null;

    // 401, 403 에러 처리
    if ([UNAUTHORIZED_ERROR_STATUS, FORBIDDEN_ERROR_STATUS].includes(errorStatus) && !errorResponse?.code) {
      return {
        code: String(errorStatus),
        message: COMMON_ERROR_MESSAGE_SYNTAX_ERROR,
        data: errorData,
      };
    }

    if (errorCode === COMMON_SYNTAX_ERROR_CODE) {
      return {
        code: String(errorCode),
        message: COMMON_ERROR_MESSAGE_SYNTAX_ERROR,
        data: errorData,
      };
    }

    if (COMMON_INTERNAL_SERVER_ERROR_CODE.includes(errorCode)) {
      return {
        code: String(errorCode),
        message: COMMON_ERROR_MESSAGE_INTERNAL_SERVER_ERROR,
        data: errorData,
      };
    }

    // 요청 시간 초과
    if (error.code === TIMEOUT_ERROR_CODE) {
      return {
        code: COMMON_CUSTOM_ERROR_CODE,
        message: COMMON_ERROR_MESSAGE_TIMEOUT,
        data: errorData,
      };
    }

    return {
      code: String(errorCode),
      message: errorMessage,
      data: errorData,
    };
  }

  return {
    code: COMMON_CUSTOM_ERROR_CODE,
    message: error instanceof Error ? error?.message : COMMON_ERROR_MESSAGE_SYNTAX_ERROR,
    data: null,
  };
};

const handleFileDownloadError = async (error: AxiosError): Promise<TypeErrorFormat> => {
  const raw = error.response?.data as { text?: () => Promise<string> } | undefined;
  if (raw?.text) {
    const text = await raw.text();
    const parsed = JSON.parse(text);

    const axiosError = new AxiosError(error.message, error.code, error.config, error.request, {
      status: error.status,
      data: {
        code: parsed.code,
        message: parsed.message,
        data: parsed.data,
      },
    } as AxiosResponse);
    return errorFormatter(axiosError);
  }
  return errorFormatter(error);
};

/**
 * http 요청을 통해 반환된 데이터를 처리하는 함수
 * @param trigger - 호출할 함수
 * @param onSuccess - 성공 시 실행할 함수
 * @param onError - 실패 시 실행할 함수
 * @param onFinally - 최종 처리 함수
 * @param config.showLoading - 로딩 인디케이터 표시 여부
 */
export const requestHelper = async <T>(
  trigger: () => Promise<AxiosResponse<T>>,
  onSuccess?: (res: T) => void | Promise<T extends { data: infer TData } ? TData : unknown>,
  onError?: (err: TypeErrorFormat) => void | Promise<void>,
  onFinally?: () => void | Promise<void>,
  config: {
    showLoading?: boolean;
    isFileDownload?: boolean;
  } = {
    showLoading: true,
    isFileDownload: false,
  }
) => {
  try {
    if (config.showLoading) {
      setLoading(true);
    }

    const response = await trigger();

    if (response?.data && !config.isFileDownload) {
      return onSuccess?.(response.data);
    }

    return onSuccess?.(response as unknown as T);
  } catch (error) {
    console.log('>>>>>> error ::', error);

    let formattedError: TypeErrorFormat;

    if (config.isFileDownload && isAxiosError(error)) {
      formattedError = await handleFileDownloadError(error);
    } else {
      formattedError = errorFormatter(error);
    }

    const isHideToastError = [
      INVALID_REFRESH_TOKEN_ERROR_CODE,
      FORBIDDEN_ERROR_STATUS,
      UNAUTHORIZED_ERROR_STATUS,
    ].includes(formattedError.code);

    /** 에러 발생 시 토스트를 표시하지 않는 경우 예외 처리  */
    if (isHideToastError) {
      return;
    }

    const isCommonError = [
      COMMON_CUSTOM_ERROR_CODE,
      COMMON_SYNTAX_ERROR_CODE,
      COMMON_INTERNAL_SERVER_ERROR_CODE,
      TIMEOUT_ERROR_CODE,
    ].includes(formattedError.code);

    /** 공통으로 처리해야 하는 에러이거나 에러 예외 처리 함수를 지정하지 않은 경우 에러 토스트 표시 */
    if (isCommonError || !onError) {
      toastService.addToast({
        message: formattedError.message,
        color: 'error',
      });
      return;
    }

    /** 서버에서 반환된 에러인 경우, 에러 코드와 메세지만 반환 */
    onError(formattedError);
  } finally {
    if (config.showLoading) {
      setLoading(false);
    }

    if (typeof onFinally === 'function') {
      onFinally();
    }
  }
};

/** @description 요청 전 빈 파라미터를 제거하는 함수 */
export const removeEmptyField = <T extends Record<string, unknown>>(params: T): T => {
  const newParams = { ...params };
  const keys = Object.keys(newParams) as (keyof T)[];

  const emptyKeys = keys.filter((key) => !newParams[key]);

  emptyKeys.forEach((key) => {
    delete newParams[key];
  });

  return newParams;
};
